import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import * as SearchFilterOptionsActions from './option-ids-visible.actions';
import {
  DateFilter,
  SearchFilter,
  SearchFilterOption,
  SearchFilterOptionsEntity,
} from './search-filter-options.models';

export const OPTION_IDS_VISIBLE_FEATURE_KEY = 'optionIdsVisible';

export interface OptionIdsVisibleInitialState extends EntityState<SearchFilterOptionsEntity> {
  selectedFacet?: SearchFilterOption; // which facet has been selected in filter-modal / only relevant for mobile
  loaded: boolean; // has the SearchFilterOptions list been loaded
  error?: string | null; // last known error (if any)
  selectedOptionIdsVisible: { [key: string]: string[] }; // all selected option ids
  dateFilter: DateFilter;
}

export interface OptionIdsVisiblePartialState {
  readonly [OPTION_IDS_VISIBLE_FEATURE_KEY]: OptionIdsVisibleInitialState;
}

export function selectOptionsIdVisible(option: SearchFilterOptionsEntity) {
  return option.key;
}

export const optionIdsVisibleInitialAdapter: EntityAdapter<SearchFilterOptionsEntity> =
  createEntityAdapter<SearchFilterOptionsEntity>({ selectId: selectOptionsIdVisible });

export const initialOptionIdsVisibleState: OptionIdsVisibleInitialState =
  optionIdsVisibleInitialAdapter.getInitialState({
    // set initial required properties
    loaded: false,
    selectedOptionIdsVisible: {},
    dateFilter: {},
  });

const reducer = createReducer(
  initialOptionIdsVisibleState,
  on(SearchFilterOptionsActions.initialOptionIdsVisibleLoadSucceeded, (state, { filterOptions }) =>
    optionIdsVisibleInitialAdapter.setAll(
      filterOptions.filter((filterOption) => filterOption.values.length > 0),
      { ...state, loaded: true }
    )
  ),
  on(SearchFilterOptionsActions.initOptionIdsVisible, (state, { selectedFilterOptions }) => {
    return {
      ...state,
      selectedOptionIdsVisible: selectedFilterOptions,
    };
  }),
  on(SearchFilterOptionsActions.initDateFilters, (state, { dateFilter }) => {
    return {
      ...state,
      dateFilter,
    };
  }),
  on(SearchFilterOptionsActions.clearSearchFilterOptions, (state) => ({
    ...state,
    selectedOptionIdsVisible: {},
  })),
  on(SearchFilterOptionsActions.clearDateFilters, (state) => ({
    ...state,
    dateFilter: {},
  })),
  on(SearchFilterOptionsActions.unselectDateFilter, (state) => ({
    ...state,
    dateFilter: {},
  })),
  on(SearchFilterOptionsActions.optionIdsVisibleSelected, (state, { searchFilterOption }) => {
    return { ...state, selectedFacet: searchFilterOption };
  }),
  on(SearchFilterOptionsActions.optionIdsVisibleUnselected, (state) => {
    return { ...state, selectedFacet: undefined };
  }),
  on(
    SearchFilterOptionsActions.optionIdsVisibleValueSelected,
    (state, { searchFilterOption, searchFilterOptionValue }) => {
      const selectedOptionIdsVisible = { ...state.selectedOptionIdsVisible };
      addOptionIdsVisibleValue(selectedOptionIdsVisible, searchFilterOption, searchFilterOptionValue);
      return { ...state, selectedOptionIdsVisible };
    }
  ),
  on(
    SearchFilterOptionsActions.optionIdsVisibleValueUnselected,
    SearchFilterOptionsActions.searchFilterOptionValueChipRemoved,
    (state, { searchFilterOption, searchFilterOptionValue }) => {
      const selectedOptionIdsVisible = { ...state.selectedOptionIdsVisible };
      removeOptionIdsVisibleValue(selectedOptionIdsVisible, searchFilterOption, searchFilterOptionValue);
      return { ...state, selectedOptionIdsVisible };
    }
  ),
  on(
    SearchFilterOptionsActions.optionIdsVisibleValuesSelected,
    (state, { searchFilterOption, searchFilterOptionValues }) => {
      const selectedOptionIdsVisible = { ...state.selectedOptionIdsVisible };
      for (const searchFilterOptionValue of searchFilterOptionValues) {
        addOptionIdsVisibleValue(selectedOptionIdsVisible, searchFilterOption, searchFilterOptionValue);
      }
      return { ...state, selectedOptionIdsVisible };
    }
  ),
  on(
    SearchFilterOptionsActions.optionIdsVisibleValuesUnselected,
    (state, { searchFilterOption, searchFilterOptionValues }) => {
      const selectedOptionIdsVisible = { ...state.selectedOptionIdsVisible };
      for (const searchFilterOptionValue of searchFilterOptionValues) {
        removeOptionIdsVisibleValue(selectedOptionIdsVisible, searchFilterOption, searchFilterOptionValue);
      }
      return { ...state, selectedOptionIdsVisible };
    }
  ),
  on(SearchFilterOptionsActions.selectedDatetimeFilterStart, (state, { lower }) => {
    return {
      ...state,
      dateFilter: {
        ...getDateFilterValue(state),
        lower,
      },
    };
  }),
  on(SearchFilterOptionsActions.selectedDatetimeFilterEnd, (state, { upper }) => {
    return {
      ...state,
      dateFilter: {
        ...getDateFilterValue(state),
        upper,
      },
    };
  })
);

export function optionIdsVisibleReducer(state: OptionIdsVisibleInitialState | undefined, action: Action) {
  return reducer(state, action);
}

function addOptionIdsVisibleValue(
  values: { [key: string]: string[] },
  searchFilterOption: SearchFilter,
  searchFilterOptionValue: { key: string }
) {
  const key = searchFilterOption.key;
  const value = searchFilterOptionValue.key;
  if (values[key] === undefined) {
    values[key] = [value];
  } else if (!values[key].includes(value)) {
    values[key] = [...values[key], value];
  }
}

function removeOptionIdsVisibleValue(
  values: { [key: string]: string[] },
  searchFilterOption: SearchFilter,
  searchFilterOptionValue: { key: string }
) {
  const key = searchFilterOption.key;
  const value = searchFilterOptionValue.key;
  if (!values[key].includes(value)) return;

  if (values[key].length > 1) {
    values[key] = values[key].filter((selectedValue) => selectedValue !== value);
  } else {
    delete values[key];
  }
}

function getDateFilterValue(state: OptionIdsVisibleInitialState) {
  if (state.dateFilter) return state.dateFilter;
  return {};
}
